/**
 * Drop inbox poll events this tab has already processed.
 *
 * The poller rewinds its cursor on every tick so a commit that landed just after
 * the previous response cannot be skipped forever. That re-delivers recent events,
 * which is harmless for alerts (they de-dupe separately) but would keep bumping
 * unread counts on the conversation list, so events are filtered by id here first.
 */

const TTL_MS = 10 * 60 * 1000;
const MAX_ENTRIES = 2000;

/** @type {Map<string, number>} */
const seen = new Map();

/**
 * @param {string} key
 * @param {number} nowMs
 * @returns {boolean} true the first time *key* is offered within the TTL
 */
function markUnseen(key, nowMs) {
  if (!key) return true;
  const at = seen.get(key);
  if (at != null && nowMs - at < TTL_MS) return false;
  seen.set(key, nowMs);
  return true;
}

function prune(nowMs) {
  if (seen.size <= MAX_ENTRIES) return;
  for (const [key, at] of seen) {
    if (nowMs - at >= TTL_MS) seen.delete(key);
  }
  while (seen.size > MAX_ENTRIES) {
    const oldest = seen.keys().next();
    if (oldest.done) break;
    seen.delete(oldest.value);
  }
}

/**
 * @param {{
 *   messages?: Array<{ session_id?: string, message_id?: string }>,
 *   handoffs?: Array<{ session_id?: string, handoff_id?: number | string }>,
 * }} payload
 * @param {number} [nowMs]
 * @returns {{ messages: Array<object>, handoffs: Array<object> }} only events not seen before
 */
export function filterUnseenPollEvents(payload, nowMs = Date.now()) {
  const messages = (payload?.messages ?? []).filter((msg) =>
    markUnseen(`msg:${msg?.message_id ?? ""}`, nowMs),
  );
  const handoffs = (payload?.handoffs ?? []).filter((hf) =>
    markUnseen(`handoff:${hf?.handoff_id ?? hf?.session_id ?? ""}`, nowMs),
  );
  prune(nowMs);
  return { messages, handoffs };
}

export function resetInboxPollEventSeen() {
  seen.clear();
}
